<template>
  <div class="lancamentos-contabeis-tab">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Carregando prévia..."></b-spinner>
      <p class="mt-2">Carregando prévia dos lançamentos contábeis...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      <h5>Erro ao carregar lançamentos</h5>
      <p>{{ error }}</p>
      <b-button variant="outline-danger" @click="carregarPrevia">
        Tentar novamente
      </b-button>
    </div>

    <!-- Conteúdo Principal -->
    <div v-else-if="previaLancamentos.habilitaLancamentos">
      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4>Lançamentos Contábeis</h4>
          <span 
            :class="['badge', 'badge-lg', !form.id ? 'badge-info' : 'badge-success']"
          >
            {{ statusPrevia }}
          </span>
        </div>
        <p class="text-muted">
          <strong>Data de Movimento:</strong> {{ dataMovimentoFormatada }} (Data de Emissão)
          <br>
          <strong>Tipo:</strong> {{ previaLancamentos.parcelado ? 'Parcelado' : 'À Vista' }}
        </p>
      </div>

      <!-- Grupos de Lançamentos -->
      <div v-for="grupo in previaLancamentos.grupos" :key="grupo" class="mb-4">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              {{ formatarTituloGrupo(grupo) }}
            </h5>
            <div class="d-flex align-items-center">
              <span 
                :class="['badge', isGrupoBalanceado(grupo) ? 'badge-success' : 'badge-danger']"
                class="mr-2"
              >
                {{ isGrupoBalanceado(grupo) ? 'Balanceado' : 'Não Balanceado' }}
              </span>
            </div>
          </div>
          
          <div class="card-body">
            <!-- Formulário para adicionar lançamento -->
            <div class="mb-4 p-3 bg-light rounded">
              <h6>Adicionar Lançamento</h6>
              <b-row>
                <b-col md="3">
                  <input-select-search
                    ref="tipoMovimento"
                    v-model="formularioLancamento.tipoMovimento"
                    label="Tipo de Movimento"
                    :options="opcoesMovimento"
                    :limparValidacao="resetarValidacao"
                    required
                  />
                </b-col>
                <b-col md="2">
                  <input-number
                    ref="valor"
                    v-model="formularioLancamento.valor"
                    label="Valor"
                    :limparValidacao="resetarValidacao"
                    decimal
                    required
                  />
                </b-col>
                <b-col md="4">
                  <input-select-search
                    ref="planoConta"
                    v-model="formularioLancamento.planoConta"
                    label="Plano de Conta"
                    :options="opcoesPlanosContas"
                    :limparValidacao="resetarValidacao"
                    required
                  />
                </b-col>
                <b-col md="2">
                  <b-button
                    variant="primary"
                    class="botao-acao-filtro w-100 mb-3"
                    @click="adicionarLancamentoFormulario(grupo)"
                    :disabled="!podeAdicionarLancamento"
                  >
                    <i class="fas fa-plus"></i> Adicionar
                  </b-button>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="12">
                  <input-text
                    ref="descricaoLancamento"
                    v-model="formularioLancamento.descricao"
                    label="Descrição do Lançamento"
                    placeholder="Descrição do lançamento"
                    :limparValidacao="resetarValidacao"
                    required
                  />
                </b-col>
              </b-row>
            </div>


            <!-- Tabela de Lançamentos -->
            <b-table
              :items="previaLancamentos.itensPorGrupo[grupo] || []"
              :fields="camposTabela"
              striped
              hover
              responsive
              class="mb-3"
              :tbody-tr-class="(item, type) => verificarTipoLancamento(item)"
            >
              <template #cell(tipo)="data">
                <span :class="['badge', data.item.tipo === 'Crédito' ? 'badge-success' : 'badge-danger']">
                  {{ data.item.tipo }}
                </span>
              </template>

              <template #cell(valor)="data">
                <span class="font-weight-bold">{{ formatarValor(data.item.valor) }}</span>
              </template>

              <template #cell(contaContabilCompleto)="data">
                <span>{{ data.item.contaContabilCompleto || `${data.item.planoContaCodigo} - ${data.item.planoContaDescricao}` }}</span>
              </template>

              <template #cell(descricao)="data">
                <span>{{ data.item.descricao || data.item.observacao || data.item.historico || '' }}</span>
              </template>

              <template #cell(acoes)="data">
                <b-dropdown variant="outline-default" size="sm">
                  <b-dropdown-item @click="editarLancamento(grupo, data.item, data.index)">
                    Editar
                  </b-dropdown-item>
                  <b-dropdown-item @click="removerLancamento(grupo, data.index)" :disabled="!data.item.editavel">
                    Excluir
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>

            <!-- Totais do Grupo -->
            <div class="row">
              <div class="col-md-6">
                <strong>Total Crédito: {{ formatarValor(calcularTotalCredito(grupo)) }}</strong>
              </div>
              <div class="col-md-6">
                <strong>Total Débito: {{ formatarValor(calcularTotalDebito(grupo)) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Totais Gerais -->
      <div class="card bg-light">
        <div class="card-body">
          <div class="row">
            <div class="col-md-4">
              <h5>Total Geral Crédito: {{ formatarValor(previaLancamentos.totalCredito) }}</h5>
            </div>
            <div class="col-md-4">
              <h5>Total Geral Débito: {{ formatarValor(previaLancamentos.totalDebito) }}</h5>
            </div>
            <div class="col-md-4">
              <span 
                :class="['badge', 'badge-lg', previaLancamentos.balanceado ? 'badge-success' : 'badge-danger']"
              >
                {{ previaLancamentos.balanceado ? 'Balanceado' : 'Não Balanceado' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Botões de Ação -->
      <div class="mt-4 text-right">
        <b-button 
          variant="outline-secondary" 
          class="mr-2"
          @click="carregarPrevia"
        >
          Recarregar Prévia
        </b-button>
        <div class="alert alert-info mt-3">
          <i class="fas fa-info-circle"></i>
          <strong>Informação:</strong> Os lançamentos contábeis serão salvos automaticamente quando você salvar a nota fiscal.
        </div>
      </div>
    </div>

    <!-- Modal de Edição -->
    <b-modal
      v-model="modalEdicaoAberto"
      title="Editar Lançamento"
      size="lg"
      @ok="salvarEdicaoLancamento"
      @cancel="cancelarEdicaoLancamento"
    >
      <div v-if="itemSelecionado">
        <b-row>
          <b-col md="6">
            <input-select-search
              ref="tipoMovimentoModal"
              v-model="itemSelecionado.tipoMovimento"
              label="Tipo de Movimento"
              :options="opcoesMovimento"
              required
            />
          </b-col>
          <b-col md="6">
            <input-number
              ref="valorModal"
              v-model="itemSelecionado.valor"
              label="Valor"
              decimal
              required
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <input-select-search
              ref="planoContaModal"
              v-model="itemSelecionado.planoConta"
              label="Plano de Conta"
              :options="opcoesPlanosContas"
              required
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <input-text
              ref="descricaoModal"
              v-model="itemSelecionado.descricao"
              label="Descrição do Lançamento"
              placeholder="Descrição do lançamento"
              required
            />
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ContabilService from '@/common/services/nota-fiscal/contabil.service.js'
import PlanoContaService from '@/common/services/plano-conta/plano-conta.service.js'
import EnumeradoresService from '@/common/services/enumeradores/enumeradores.service'
import mensagem from '@/common/utils/mensagem'
import helpers from '@/common/utils/helpers'
import { START_LOADING, STOP_LOADING } from '@/store/Store'
import { InputSelectSearch, InputNumber, InputText } from '@/components/inputs'

export default {
  name: 'LancamentosContabeisTab',
  components: {
    InputSelectSearch,
    InputNumber,
    InputText
  },
  props: {
    dataEmissao: {
      type: [String, Date],
      required: false,
      default: null
    },
    form: {
      type: Object,
      required: false,
      default: () => ({})
    },
    abaAtiva: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      previaLancamentos: {
        habilitaLancamentos: false,
        parcelado: false,
        grupos: [],
        itensPorGrupo: {},
        balanceado: false,
        totalCredito: 0,
        totalDebito: 0
      },
      camposTabela: [
        { key: 'tipo', label: 'Tipo', class: 'text-center' },
        { key: 'valor', label: 'Valor', class: 'text-right' },
        { key: 'contaContabilCompleto', label: 'Plano de Conta' },
        { key: 'descricao', label: 'Descrição', class: 'text-left' },
        { key: 'acoes', label: 'Ações', class: 'text-center' }
      ],
      opcoesMovimento: [],
      opcoesPlanosContas: [],
      // Variáveis para modal de edição
      itemSelecionado: null,
      grupoSelecionado: null,
      indexSelecionado: null,
      // Variáveis para formulário de adição
      formularioLancamento: {
        tipoMovimento: '',
        valor: null,
        descricao: '',
        planoConta: ''
      },
      resetarValidacao: false,
      modalEdicaoAberto: false
    }
  },
  computed: {
    dataMovimentoFormatada() {
      if (!this.dataEmissao) return ''
      const data = new Date(this.dataEmissao)
      return data.toLocaleDateString('pt-BR')
    },
    statusPrevia() {
      return 'Lançamentos contábeis'
    },
    podeAdicionarLancamento() {
      return this.formularioLancamento.tipoMovimento && 
             this.formularioLancamento.valor > 0 && 
             this.formularioLancamento.planoConta &&
             this.formularioLancamento.descricao
    },
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    }
  },
  watch: {
    // Carregar prévia apenas quando a aba for ativada e for um rascunho
    abaAtiva(novaValor) {
      if (novaValor && !this.form.id) {
        // Só carregar prévia se for um rascunho (sem ID)
        this.carregarPrevia()
      }
    }
  },
  mounted() {
    this.carregarTiposMovimento()
    this.carregarPlanosContas()
    
    // Só carregar prévia se a aba já estiver ativa e for um rascunho
    if (this.abaAtiva && !this.form.id) {
      this.$nextTick(() => {
        this.carregarPrevia()
      })
    }
  },
  methods: {
    async carregarPrevia() {
      this.loading = true
      this.error = null

      try {
        // Verificar se o form tem dados mínimos
        if (!this.form || !this.form.dataEmissao || !this.form.tipoCompra || !this.form.tipoNotaFiscal) {
          console.warn('[previa] Form ainda não está pronto, aguardando...')
          this.loading = false
          return
        }
        
        // Usar endpoint com dados do form (rascunho)
        const draft = this.montarDraftAPartirDoForm(this.form)
        if (process.env.NODE_ENV === 'development') {
          console.debug('[previa] POST rascunho', `/api/v1/NotaFiscal/contabil/previa`, draft)
        }
        const response = await ContabilService.postPreviaDesdeRascunho(draft)
        this.previaLancamentos = response.data || response
        
        this.recalcularTotais()
      } catch (err) {
        console.error('Erro ao carregar prévia:', err)
        
        if (err.response?.status === 404) {
          this.error = 'Nota fiscal não encontrada'
        } else if (err.response?.status === 400) {
          this.error = err.response.data?.message || 'Dados inválidos'
        } else {
          this.error = 'Erro ao carregar prévia dos lançamentos'
        }
        
        mensagem.showErrors(err)
      } finally {
        this.loading = false
      }
    },


    adicionarLancamento(grupo) {
      const novoLancamento = {
        grupoId: grupo,
        dataMovimento: this.formatarDataParaAPI(this.dataEmissao),
        valor: 0,
        tipo: 'Débito',
        planoContaCodigo: '',
        planoContaDescricao: '',
        historico: '',
        descricao: '',
        contaContabilCompleto: '',
        editavel: true
      }

      if (!this.previaLancamentos.itensPorGrupo[grupo]) {
        this.$set(this.previaLancamentos.itensPorGrupo, grupo, [])
      }

      this.previaLancamentos.itensPorGrupo[grupo].push(novoLancamento)
      this.recalcularTotais()
    },

    adicionarLancamentoFormulario(grupo) {
      if (!this.podeAdicionarLancamento) return

      const novoLancamento = {
        grupoId: grupo,
        dataMovimento: this.formatarDataParaAPI(this.dataEmissao),
        valor: Number(this.formularioLancamento.valor),
        tipo: this.formularioLancamento.tipoMovimento.descricao,
        tipoMovimento: this.formularioLancamento.tipoMovimento.id,
        tipoMovimentoDescricao: this.formularioLancamento.tipoMovimento.descricao,
        descricao: this.formularioLancamento.descricao,
        planoContaId: this.formularioLancamento.planoConta.id,
        contaContabilCompleto: `${this.formularioLancamento.planoConta.codigo} - ${this.formularioLancamento.planoConta.descricao}`,
        planoContaCodigo: this.formularioLancamento.planoConta.codigo,
        planoContaDescricao: this.formularioLancamento.planoConta.descricao,
        editavel: true
      }

      if (!this.previaLancamentos.itensPorGrupo[grupo]) {
        this.$set(this.previaLancamentos.itensPorGrupo, grupo, [])
      }

      this.previaLancamentos.itensPorGrupo[grupo].push(novoLancamento)
      this.recalcularTotais()
      this.limparFormularioLancamento()
    },

    editarLancamento(grupo, item, index) {
      // Encontrar o tipo de movimento correspondente
      const tipoMovimento = this.opcoesMovimento.find(t => t.text === item.tipo) || 
                           this.opcoesMovimento.find(t => t.value === item.tipo)
      
      // Encontrar o plano de conta correspondente  
      const planoConta = this.opcoesPlanosContas.find(p => p.id === item.planoContaId) ||
                        this.opcoesPlanosContas.find(p => p.text === item.contaContabilCompleto)

      this.itemSelecionado = {
        ...item,
        tipoMovimento: tipoMovimento || '',
        planoConta: planoConta || ''
      }
      this.grupoSelecionado = grupo
      this.indexSelecionado = index
      this.modalEdicaoAberto = true
    },

    salvarEdicaoLancamento() {
      if (!this.itemSelecionado || !this.grupoSelecionado || this.indexSelecionado === null) return

      // Obter dados do tipo de movimento
      let tipoDescricao = this.itemSelecionado.tipo
      let tipoMovimentoId = this.itemSelecionado.tipoMovimento
      
      if (this.itemSelecionado.tipoMovimento && typeof this.itemSelecionado.tipoMovimento === 'object') {
        tipoDescricao = this.itemSelecionado.tipoMovimento.descricao || this.itemSelecionado.tipoMovimento.text
        tipoMovimentoId = this.itemSelecionado.tipoMovimento.id
      }

      // Obter dados do plano de conta
      let planoContaId = this.itemSelecionado.planoContaId
      let planoContaDescricao = this.itemSelecionado.planoContaDescricao || ''
      let planoContaCodigo = this.itemSelecionado.planoContaCodigo || ''
      let contaContabilCompleto = this.itemSelecionado.contaContabilCompleto || ''
      
      if (this.itemSelecionado.planoConta && typeof this.itemSelecionado.planoConta === 'object') {
        planoContaId = this.itemSelecionado.planoConta.id
        planoContaDescricao = this.itemSelecionado.planoConta.descricao || ''
        planoContaCodigo = this.itemSelecionado.planoConta.codigo || ''
        contaContabilCompleto = `${planoContaCodigo} - ${planoContaDescricao}`
      }

      // Atualizar dados baseados nos objetos selecionados
      const itemAtualizado = {
        ...this.itemSelecionado,
        tipo: tipoDescricao,
        tipoMovimento: tipoMovimentoId,
        tipoMovimentoDescricao: tipoDescricao,
        planoContaId: planoContaId,
        planoContaDescricao: planoContaDescricao,
        planoContaCodigo: planoContaCodigo,
        contaContabilCompleto: contaContabilCompleto
      }

      // Atualizar o item na lista
      this.$set(
        this.previaLancamentos.itensPorGrupo[this.grupoSelecionado], 
        this.indexSelecionado, 
        itemAtualizado
      )
      
      this.recalcularTotais()
      this.cancelarEdicaoLancamento()
    },

    cancelarEdicaoLancamento() {
      this.itemSelecionado = null
      this.grupoSelecionado = null
      this.indexSelecionado = null
      this.modalEdicaoAberto = false
    },

    limparFormularioLancamento() {
      this.formularioLancamento = {
        tipoMovimento: '',
        valor: null,
        descricao: '',
        planoConta: ''
      }
      this.resetarValidacao = true
      setTimeout(() => {
        this.resetarValidacao = false
      }, 50)
    },

    verificarTipoLancamento(item) {
      if (!item || !item.tipo) return ''
      
      const tipo = item.tipo.toString().trim().toLowerCase()
      
      // Verificar diferentes variações de crédito
      if (tipo === 'crédito' || tipo === 'credito' || tipo === 'c') {
        return 'linha-credito'
      }
      
      // Verificar diferentes variações de débito
      if (tipo === 'débito' || tipo === 'debito' || tipo === 'd') {
        return 'linha-debito'
      }
      
      return ''
    },

    carregarTiposMovimento() {
      this.$store.dispatch(START_LOADING)
      EnumeradoresService.listar('tipo-movimento')
        .then(({ data }) => {
          this.opcoesMovimento = data.map((row) => ({
            ...row,
            value: row,
            text: row.descricao,
          }))
        })
        .catch((err) => {
          mensagem.showErrors(err)
        })
        .finally(() => {
          this.$store.dispatch(STOP_LOADING)
        })
    },

    carregarPlanosContas() {
      this.$store.dispatch(START_LOADING)
      PlanoContaService.obterTodos()
        .then(({ data }) => {
          this.opcoesPlanosContas = data.map((row) => ({
            ...row,
            value: row,
            text: `${row.codigo} - ${row.descricao}`,
          }))
        })
        .catch((err) => {
          mensagem.showErrors(err)
        })
        .finally(() => {
          this.$store.dispatch(STOP_LOADING)
        })
    },

    // Método para obter lançamentos contábeis formatados para o backend
    obterLancamentosContabeisParaBackend() {
      const lancamentos = []
      
      
      this.previaLancamentos.grupos.forEach(grupo => {
        const itens = this.previaLancamentos.itensPorGrupo[grupo] || []
        itens.forEach(item => {
          
          // Enviar TODOS os lançamentos, não apenas os editáveis
          if (item.tipo && item.valor > 0) {
            // Encontrar o plano de conta correspondente se não estiver preenchido
            let planoContaId = item.planoContaId
            let planoContaDescricao = item.planoContaDescricao || ''
            let contaContabilCompleto = item.contaContabilCompleto || ''
            
            // Se não tem planoContaId, tentar encontrar pelo planoConta
            if (!planoContaId && item.planoConta && item.planoConta.id) {
              planoContaId = item.planoConta.id
              planoContaDescricao = item.planoConta.descricao || ''
              contaContabilCompleto = `${item.planoConta.codigo} - ${item.planoConta.descricao}`
            }
            
            // Se ainda não tem planoContaId, tentar encontrar nas opções
            if (!planoContaId && item.contaContabilCompleto) {
              const planoEncontrado = this.opcoesPlanosContas.find(p => p.text === item.contaContabilCompleto)
              if (planoEncontrado) {
                planoContaId = planoEncontrado.id
                planoContaDescricao = planoEncontrado.descricao
                contaContabilCompleto = planoEncontrado.text
              }
            }
            
            // Se ainda não tem planoContaId, tentar encontrar pelo código e descrição
            if (!planoContaId && item.planoContaCodigo && item.planoContaDescricao) {
              const planoEncontrado = this.opcoesPlanosContas.find(p => 
                p.codigo === item.planoContaCodigo && p.descricao === item.planoContaDescricao
              )
              if (planoEncontrado) {
                planoContaId = planoEncontrado.id
                planoContaDescricao = planoEncontrado.descricao
                contaContabilCompleto = `${planoEncontrado.codigo} - ${planoEncontrado.descricao}`
              }
            }
            
            // Se ainda não tem planoContaId, tentar encontrar pelo código nas opções
            if (!planoContaId && item.planoContaCodigo) {
              // Tentar diferentes formatos de busca
              let planoEncontrado = this.opcoesPlanosContas.find(p => p.codigo === item.planoContaCodigo)
              
              // Se não encontrar, tentar como string
              if (!planoEncontrado) {
                planoEncontrado = this.opcoesPlanosContas.find(p => String(p.codigo) === String(item.planoContaCodigo))
              }
              
              // Se ainda não encontrar, tentar por descrição
              if (!planoEncontrado && item.planoContaDescricao) {
                planoEncontrado = this.opcoesPlanosContas.find(p => 
                  p.descricao && p.descricao.toLowerCase().includes(item.planoContaDescricao.toLowerCase())
                )
              }
              
              if (planoEncontrado) {
                planoContaId = planoEncontrado.id
                planoContaDescricao = planoEncontrado.descricao
                contaContabilCompleto = `${planoEncontrado.codigo} - ${planoEncontrado.descricao}`
              } else {
                // Se não encontrar, pular este item pois precisa de um GUID válido
                return
              }
            }
            
            // Validar se planoContaId é um GUID válido
            const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(planoContaId)
            
            
            // Só adicionar se tiver dados válidos e planoContaId for um GUID
            if (planoContaId && item.tipo && isGuid) {
              // Converter para o formato esperado pelo backend (sem acentos)
              const tipoOriginal = item.tipoMovimentoDescricao || item.tipo || ''
              const tipoMovimentoBackend = tipoOriginal.toLowerCase()
                .replace('crédito', 'credito')
                .replace('débito', 'debito')
                .replace('credito', 'Credito')
                .replace('debito', 'Debito')
              
              
              const lancamento = {
                tipoMovimento: tipoMovimentoBackend,
                tipoMovimentoDescricao: item.tipoMovimentoDescricao || item.tipo,
                valor: Number(item.valor) || 0,
                planoContaId: planoContaId,
                planoContaDescricao: planoContaDescricao,
                contaContabilCompleto: contaContabilCompleto,
                descricao: item.descricao || item.observacao || item.historico || '',
                origem: grupo
              }
              
              // Só incluir id se não for null/undefined
              if (item.id && item.id !== null && item.id !== 'null') {
                lancamento.id = item.id
              }
              
              lancamentos.push(lancamento)
            }
          }
        })
      })
      
      
      return lancamentos
    },

    // Método público para o componente pai obter os lançamentos
    obterLancamentosContabeis() {
      return this.obterLancamentosContabeisParaBackend()
    },

    removerLancamento(grupo, index) {
      if (this.previaLancamentos.itensPorGrupo[grupo]) {
        this.previaLancamentos.itensPorGrupo[grupo].splice(index, 1)
        this.recalcularTotais()
      }
    },

    recalcularTotais() {
      let totalCredito = 0
      let totalDebito = 0

      this.previaLancamentos.grupos.forEach(grupo => {
        const itens = this.previaLancamentos.itensPorGrupo[grupo] || []
        itens.forEach(item => {
          if (item.tipo === 'Crédito') {
            totalCredito += Number(item.valor) || 0
          } else if (item.tipo === 'Débito') {
            totalDebito += Number(item.valor) || 0
          }
        })
      })

      this.previaLancamentos.totalCredito = totalCredito
      this.previaLancamentos.totalDebito = totalDebito
      this.previaLancamentos.balanceado = Math.abs(totalCredito - totalDebito) < 0.01
    },

    calcularTotalCredito(grupo) {
      const itens = this.previaLancamentos.itensPorGrupo[grupo] || []
      return itens
        .filter(item => item.tipo === 'Crédito')
        .reduce((total, item) => total + (Number(item.valor) || 0), 0)
    },

    calcularTotalDebito(grupo) {
      const itens = this.previaLancamentos.itensPorGrupo[grupo] || []
      return itens
        .filter(item => item.tipo === 'Débito')
        .reduce((total, item) => total + (Number(item.valor) || 0), 0)
    },

    isGrupoBalanceado(grupo) {
      const totalCredito = this.calcularTotalCredito(grupo)
      const totalDebito = this.calcularTotalDebito(grupo)
      return Math.abs(totalCredito - totalDebito) < 0.01
    },

    formatarTituloGrupo(grupo) {
      if (grupo === 'unico') {
        return 'Lançamento Único'
      }
      return grupo.charAt(0).toUpperCase() + grupo.slice(1).replace('-', ' ')
    },

    formatarValor(valor) {
      return helpers.formatacaoMoedaBRL(valor || 0)
    },

    formatarData(data) {
      if (!data) return ''
      
      // Se for string no formato YYYY-MM-DD (DateOnly)
      if (typeof data === 'string' && data.includes('-')) {
        const [ano, mes, dia] = data.split('-')
        return `${dia}/${mes}/${ano}`
      }
      
      // Se for Date object
      if (data instanceof Date) {
        return data.toLocaleDateString('pt-BR')
      }
      
      return data
    },

    formatarDataParaAPI(data) {
      if (!data) return ''
      
      const dataObj = new Date(data)
      const ano = dataObj.getFullYear()
      const mes = String(dataObj.getMonth() + 1).padStart(2, '0')
      const dia = String(dataObj.getDate()).padStart(2, '0')
      
      return `${ano}-${mes}-${dia}`
    },

    montarDraftAPartirDoForm(form) {
      // Validar dados mínimos
      if (!form.dataEmissao || !form.tipoCompra || !form.tipoNotaFiscal) {
        throw new Error('Dados insuficientes no formulário para gerar prévia')
      }

      // Mapear impostos
      const impostos = (form.impostos || []).map(imposto => ({
        tipoImposto: imposto.tipoImposto || imposto.tipo,
        valor: Number(imposto.valor) || 0
      }))

      // Mapear parcelas
      const parcelas = (form.notasFiscaisParcelas || []).map(parcela => ({
        valor: Number(parcela.valorDocumento) || 0,
        dataVencimento: parcela.dataVencimento || form.dataVencimento
      }))

      // Determinar forma de pagamento
      const forma = parcelas.length > 1 ? 'Parcelado' : 'Avista'

      // Mapear itens (se for compra de produto)
      const itens = (form.notasFiscaisItens || []).map(item => ({
        produtoId: item.produtoId,
        quantidade: Number(item.quantidade) || 0,
        valorUnitario: Number(item.valorUnitario) || 0
      }))

      // Calcular valores
      const valorBruto = Number(form.valorBruto) || 0
      const valorTotalImpostos = impostos.reduce((total, imposto) => total + imposto.valor, 0)
      const descontos = Number(form.descontos) || 0
      const valorLiquido = valorBruto - valorTotalImpostos - descontos

      // Obter histórico de lançamento
      const historicoLancamentoId = form.historicoLancamento?.value || 
                                   form.historicoLancamentoId ||
                                   (parcelas.length > 0 ? parcelas[0].historicoLancamentoId : null)

      const draft = {
        tipoNotaFiscal: Number(form.tipoNotaFiscal) || 319,
        tipoCompra: Number(form.tipoCompra) || 1,
        dataEmissao: form.dataEmissao,
        fornecedorId: form.fornecedorId,
        impostos: impostos,
        pagamento: {
          forma: forma,
          valorBruto: valorBruto,
          valorLiquido: valorLiquido,
          parcelas: parcelas
        },
        itens: itens,
        historicoLancamentoId: historicoLancamentoId
      }

      if (process.env.NODE_ENV === 'development') {
        console.debug('[draft] Montado:', draft)
      }

      return draft
    }
  }
}
</script>

<style scoped>
.lancamentos-contabeis-tab {
  padding: 1rem;
}

.badge-lg {
  font-size: 1rem;
  padding: 0.5rem 1rem;
}

.card-header h5 {
  color: #495057;
}

.table th {
  border-top: none;
  font-weight: 600;
  background-color: #f8f9fa;
}

.alert h5 {
  margin-bottom: 0.5rem;
}

.text-right input {
  text-align: right;
}

/* Cores para linhas de crédito e débito - Alta especificidade */
.lancamentos-contabeis-tab .table.table-striped tbody tr.linha-credito {
  background-color: rgba(40, 167, 69, 0.2) !important;
}

.lancamentos-contabeis-tab .table.table-striped tbody tr.linha-debito {
  background-color: rgba(220, 53, 69, 0.2) !important;
}

/* Força a aplicação das cores nas células */
.lancamentos-contabeis-tab .table.table-striped tbody tr.linha-credito td {
  background-color: rgba(40, 167, 69, 0.2) !important;
  border-color: rgba(40, 167, 69, 0.3) !important;
}

.lancamentos-contabeis-tab .table.table-striped tbody tr.linha-debito td {
  background-color: rgba(220, 53, 69, 0.2) !important;
  border-color: rgba(220, 53, 69, 0.3) !important;
}

/* Hover states */
.lancamentos-contabeis-tab .table.table-striped tbody tr.linha-credito:hover {
  background-color: rgba(40, 167, 69, 0.3) !important;
}

.lancamentos-contabeis-tab .table.table-striped tbody tr.linha-debito:hover {
  background-color: rgba(220, 53, 69, 0.3) !important;
}

.lancamentos-contabeis-tab .table.table-striped tbody tr.linha-credito:hover td {
  background-color: rgba(40, 167, 69, 0.3) !important;
}

.lancamentos-contabeis-tab .table.table-striped tbody tr.linha-debito:hover td {
  background-color: rgba(220, 53, 69, 0.3) !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}
</style>
